import {
    IOption,
    IOptionSet,
    ISelectedOptionSet,
    OptionSelectionMode
} from "../interfaces/IOptionSet";

export const resolveOptionSelectionMode = (value: unknown): OptionSelectionMode =>
    typeof value === 'string' && value.trim().toUpperCase() === 'MULTIPLE'
        ? 'MULTIPLE'
        : 'SINGLE';

export const allowsMultipleSelections = (optionSet: IOptionSet): boolean =>
    resolveOptionSelectionMode(optionSet.selectionMode) === 'MULTIPLE';

export const resolveOptionSetRequired = (value: unknown): boolean =>
    value !== false;

export const isOptionSetRequired = (optionSet: IOptionSet): boolean =>
    resolveOptionSetRequired(optionSet.isRequired);

export const isOptionActive = (option: IOption): boolean => option.active !== false && !option.deleted;

export const findUnavailableRequiredOptionSet = (optionSets: IOptionSet[]): IOptionSet | undefined =>
    optionSets.find(set => isOptionSetRequired(set) && !(set.options ?? []).some(isOptionActive));

const matchesOption = (selection: ISelectedOptionSet, option: IOption): boolean => {
    // Older options use positional IDs. Match their saved name when available so
    // deleting/reordering a row cannot silently select a different option.
    if (selection.optionId && /^option-\d+$/.test(selection.optionId) && selection.optionName) {
        return selection.optionName === option.name;
    }
    if (selection.optionId && option.id) return selection.optionId === option.id;
    return selection.optionName !== undefined && selection.optionName === option.name;
};

const toSelectedOption = (optionSet: IOptionSet, option: IOption): ISelectedOptionSet => ({
    optionSetId: optionSet.id,
    optionSetName: optionSet.name,
    optionId: option.id,
    optionName: option.name,
    additionalPrice: Number(option.additionalPrice ?? 0)
});

export const updateOptionSelections = (
    existingSelections: ISelectedOptionSet[],
    optionSet: IOptionSet,
    optionId: string
): ISelectedOptionSet[] => {
    const selectedOption = (optionSet.options ?? []).find(option => option.id === optionId);
    if (!selectedOption || !isOptionActive(selectedOption)) {
        return existingSelections;
    }

    if (!allowsMultipleSelections(optionSet)) {
        return [
            ...existingSelections.filter(selection => selection.optionSetId !== optionSet.id),
            toSelectedOption(optionSet, selectedOption)
        ];
    }

    const isSelected = existingSelections.some(selection =>
        selection.optionSetId === optionSet.id && matchesOption(selection, selectedOption)
    );

    if (isSelected) {
        return existingSelections.filter(selection =>
            selection.optionSetId !== optionSet.id || !matchesOption(selection, selectedOption)
        );
    }

    return [...existingSelections, toSelectedOption(optionSet, selectedOption)];
};

export const clearOptionSetSelections = (
    existingSelections: ISelectedOptionSet[],
    optionSetId: string | undefined
): ISelectedOptionSet[] =>
    existingSelections.filter(selection => selection.optionSetId !== optionSetId);

export const findMissingRequiredOptionSet = (
    optionSets: IOptionSet[],
    selectedOptions: ISelectedOptionSet[]
): IOptionSet | undefined => optionSets.find(optionSet =>
    isOptionSetRequired(optionSet) &&
    !selectedOptions.some(selection =>
        selection.optionSetId === optionSet.id &&
        (optionSet.options ?? []).some(option => isOptionActive(option) && matchesOption(selection, option))
    )
);

export const reconcileOptionSelections = (
    optionSets: IOptionSet[],
    existingSelections: ISelectedOptionSet[],
    selectDefaults: boolean = true
): ISelectedOptionSet[] => optionSets.flatMap(optionSet => {
    const options = (optionSet.options ?? []).filter(isOptionActive);
    const selectionsForSet = existingSelections.filter(selection =>
        selection.optionSetId === optionSet.id
    );

    if (allowsMultipleSelections(optionSet)) {
        return selectionsForSet.flatMap(selection => {
            const option = options.find(candidate => matchesOption(selection, candidate));
            return option ? [toSelectedOption(optionSet, option)] : [];
        });
    }

    const selectedOption = selectionsForSet
        .map(selection => options.find(option => matchesOption(selection, option)))
        .find((option): option is IOption => option !== undefined) ??
        (selectDefaults && selectionsForSet.length === 0 && isOptionSetRequired(optionSet) ? options[0] : undefined);

    return selectedOption ? [toSelectedOption(optionSet, selectedOption)] : [];
});
